/**
 * Client-side rate limiter to prevent brute force attacks
 */
class RateLimiter {
  constructor(maxAttempts = 5, timeWindowMs = 300000) { // 5 attempts per 5 minutes
    this.maxAttempts = maxAttempts
    this.timeWindowMs = timeWindowMs
    this.attempts = []
  }

  /**
   * Records a login attempt
   */
  recordAttempt() {
    const now = Date.now()
    this.attempts.push(now)
    
    // Clean old attempts outside the time window
    this.cleanOldAttempts()
    
    // Store in localStorage for persistence
    this.saveToStorage()
  }

  /**
   * Checks if rate limit is exceeded
   * @returns {boolean} True if rate limit is exceeded
   */
  isRateLimited() {
    this.cleanOldAttempts()
    return this.attempts.length >= this.maxAttempts
  }

  /**
   * Gets remaining time until rate limit resets
   * @returns {number} Milliseconds until reset
   */
  getTimeUntilReset() {
    if (this.attempts.length === 0) return 0
    
    const oldestAttempt = this.attempts[0]
    const resetTime = oldestAttempt + this.timeWindowMs
    const now = Date.now()
    
    return Math.max(0, resetTime - now)
  }

  /**
   * Gets formatted time until reset
   * @returns {string} Human-readable time
   */
  getFormattedTimeUntilReset() {
    const ms = this.getTimeUntilReset()
    const minutes = Math.ceil(ms / 60000)
    
    if (minutes === 1) return '1 minuto'
    return `${minutes} minutos`
  }

  /**
   * Removes attempts outside the time window
   */
  cleanOldAttempts() {
    const now = Date.now()
    this.attempts = this.attempts.filter(
      timestamp => now - timestamp < this.timeWindowMs
    )
    this.saveToStorage()
  }

  /**
   * Resets all attempts
   */
  reset() {
    this.attempts = []
    this.saveToStorage()
  }

  /**
   * Saves attempts to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem('loginAttempts', JSON.stringify(this.attempts))
    } catch (e) {
      // If localStorage is not available, continue without persistence
    }
  }

  /**
   * Loads attempts from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('loginAttempts')
      if (stored) {
        this.attempts = JSON.parse(stored)
        this.cleanOldAttempts()
      }
    } catch (e) {
      this.attempts = []
    }
  }
}

// Create singleton instance
const rateLimiter = new RateLimiter()
rateLimiter.loadFromStorage()

export default rateLimiter
